import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, CalendarDays } from './icons';
import MultiSelectDropdown from './MultiSelectDropdown';
import { formatDateToYMD, formatRelativeTime, formatDisplayDateRange } from '../utils/formatters';

// Helper to determine which date preset is active based on the date range
const getActivePresetForDateRange = (dateRange) => {
    const today = new Date();
    const todayYMD = formatDateToYMD(today);

    // Check for 'thisMonth'
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (dateRange.startDate === formatDateToYMD(firstDayOfMonth) && dateRange.endDate === todayYMD) {
        return 'thisMonth';
    }

    // Check for 'today'
    if (dateRange.startDate === todayYMD && dateRange.endDate === todayYMD) {
        return 'today';
    }
    
    // Check for 'last7'
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    if (dateRange.startDate === formatDateToYMD(sevenDaysAgo) && dateRange.endDate === formatDateToYMD(yesterday)) {
        return 'last7';
    }
    
    // Check for 'last30'
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    if (dateRange.startDate === formatDateToYMD(thirtyDaysAgo) && dateRange.endDate === formatDateToYMD(yesterday)) {
        return 'last30';
    }
    
    return null; // It's a custom range
};


const Header = ({ 
    onFiltersApply, 
    currentFilters,
    availableLocations,
    availableSalesPersons,
    lastUpdated,
    isRefreshing,
    onManualRefresh,
    error,
}) => {
    const [stagedDateRange, setStagedDateRange] = useState(currentFilters.dateRange);
    const [activePreset, setActivePreset] = useState(() => getActivePresetForDateRange(currentFilters.dateRange));
    const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
    const [customDateDisplay, setCustomDateDisplay] = useState(() => {
        if (getActivePresetForDateRange(currentFilters.dateRange) === null) {
            return formatDisplayDateRange(currentFilters.dateRange.startDate, currentFilters.dateRange.endDate);
        }
        return null;
    });
    const datePickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setIsDatePopoverOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Sync local state with props from parent, which might come from localStorage
    useEffect(() => {
        setStagedDateRange(currentFilters.dateRange);
        const newActivePreset = getActivePresetForDateRange(currentFilters.dateRange);
        setActivePreset(newActivePreset);
        if (newActivePreset === null) {
            setCustomDateDisplay(formatDisplayDateRange(currentFilters.dateRange.startDate, currentFilters.dateRange.endDate));
        } else {
            setCustomDateDisplay(null);
        }
    }, [currentFilters.dateRange]);

    const handleCustomDateApply = () => {
        if (stagedDateRange.startDate && stagedDateRange.endDate && stagedDateRange.startDate <= stagedDateRange.endDate) {
            onFiltersApply({ dateRange: stagedDateRange });
            setCustomDateDisplay(formatDisplayDateRange(stagedDateRange.startDate, stagedDateRange.endDate));
            setIsDatePopoverOpen(false);
        } else {
            console.error("Invalid date range selected in popover.");
        }
    };

    const handleDateChange = (field, value) => {
        setStagedDateRange(prev => ({ ...prev, [field]: value }));
        setActivePreset(null);
    };

    const handleLocationChange = (selected) => {
        onFiltersApply({ locations: selected });
    };

    const handleSalesPersonChange = (selected) => {
        onFiltersApply({ salesPersons: selected });
    };

    const handlePresetClick = (preset) => {
        const today = new Date();
        let startDate = new Date();
        let endDate = new Date();

        switch (preset) {
            case 'today':
                startDate = today;
                endDate = today;
                break;
            case 'last7':
                endDate.setDate(today.getDate() - 1); // Yesterday
                startDate.setDate(today.getDate() - 7); // 7 days ago
                break;
                
            case 'last30':
                endDate.setDate(today.getDate() - 1); // Yesterday
                startDate.setDate(today.getDate() - 30); // 30 days ago
                break;

            case 'thisMonth':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1); // First day of current month
                endDate = today; // Today
                break;
            default:
                startDate = today;
                endDate = today;
                break;
        }

        const newDateRange = { 
            startDate: formatDateToYMD(startDate), 
            endDate: formatDateToYMD(endDate) 
        };
        setActivePreset(preset);
        setCustomDateDisplay(null);
        setIsDatePopoverOpen(false);
        onFiltersApply({ dateRange: newDateRange });
    };
    
    const presetButtons = [
        { key: 'today', icon: <CalendarDays className="w-5 h-5" title="Today" />, label: 'Today' },
        { key: 'last7', label: 'Last 7 days' },
        { key: 'last30', label: 'Last 30 days' },
        { key: 'thisMonth', label: 'This Month' },
    ];

    const getStatus = () => {
        if (error) {
            return { color: 'bg-red-500', text: 'Update failed' };
        }
        if (!lastUpdated) {
            return { color: 'bg-yellow-500', text: 'Updating...' };
        }
        const isFresh = (new Date().getTime() - lastUpdated.getTime()) < 60000;
        const relativeTime = formatRelativeTime(lastUpdated);
        if (isFresh) {
            return { color: 'bg-green-500', text: `Last updated: ${relativeTime}` };
        }
        return { color: 'bg-red-500', text: `Last updated: ${relativeTime}` };
    };
    const status = getStatus();

    return (
        <header className="bg-white border-b border-slate-200 px-4 py-3 md:px-6 lg:px-8 mt-20">
            {/* Top row: Title and User Info */}
            <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
                <div className="text-sm text-slate-500 mt-1">
                    <div className="flex items-center">
                         <span className={`w-2 h-2 rounded-full mr-2 ${status.color}`} title={status.color === 'bg-green-500' ? 'Data is fresh' : 'Data is stale or an error occurred'}></span>
                        <span className="truncate">{status.text}</span>
                        <button 
                            onClick={onManualRefresh}
                            disabled={isRefreshing}
                            className="ml-2 p-1 rounded-full hover:bg-slate-100 disabled:cursor-not-allowed"
                            aria-label="Refresh data"
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-4 h-4 text-slate-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center">
                            <span className="font-semibold text-blue-700">AT</span>
                        </div>
                        <div className='hidden sm:block'>
                            <p className="font-semibold text-slate-700">Aayant Todi</p>
                        </div>
                    </div>
                </div> */}
            {/* </div> */}

            {/* Bottom row: Filters */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                <MultiSelectDropdown
                    label="Location"
                    options={availableLocations}
                    selectedOptions={currentFilters.locations}
                    onChange={handleLocationChange}
                />
                <MultiSelectDropdown
                    label="Sales Person"
                    options={availableSalesPersons}
                    selectedOptions={currentFilters.salesPersons}
                    onChange={handleSalesPersonChange}
                />
                
                <div className="flex items-center p-1 rounded-lg bg-slate-100">
                    {presetButtons.map(({ key, label, icon }) => (
                         <button
                            key={key}
                            onClick={() => handlePresetClick(key)}
                            title={label}
                            aria-pressed={activePreset === key}
                            className={`text-sm font-medium rounded-md transition-colors ${
                                activePreset === key 
                                ? 'bg-white text-blue-600 shadow-sm' 
                                : 'text-slate-600 hover:bg-slate-200'
                            } ${
                                icon ? 'p-2' : 'px-3 py-1'
                            }`}
                        >
                            {icon ? icon : label}
                        </button>
                    ))}
                    <div className="relative" ref={datePickerRef}>
                         <button
                            onClick={() => setIsDatePopoverOpen(prev => !prev)}
                            aria-pressed={activePreset === null}
                            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center ${
                                activePreset === null || isDatePopoverOpen
                                ? 'bg-white text-blue-600 shadow-sm' 
                                : 'text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Customise Date
                        </button>
                        {isDatePopoverOpen && (
                            <div className="origin-top-right absolute right-0 mt-2 w-auto rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20 p-4">
                                <div className="flex items-center space-x-2">
                                    <label htmlFor="start-date" className="sr-only">Start Date</label>
                                    <input
                                        id="start-date"
                                        type="date"
                                        max={new Date().toISOString().split("T")[0]}
                                        value={stagedDateRange.startDate}
                                        onChange={(e) => handleDateChange('startDate', e.target.value)}
                                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition w-36"
                                        aria-label="Start Date"
                                    />
                                    <span className="text-slate-500">-</span>
                                    <label htmlFor="end-date" className="sr-only">End Date</label>
                                    <input
                                        id="end-date"
                                        type="date"
                                        max={new Date().toISOString().split("T")[0]}
                                        value={stagedDateRange.endDate}
                                        onChange={(e) => handleDateChange('endDate', e.target.value)}
                                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition w-36"
                                        aria-label="End Date"
                                    />
                                </div>
                                <button
                                    onClick={handleCustomDateApply}
                                    className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Apply
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                 {customDateDisplay && (
                    <span className="text-sm font-semibold text-blue-600 ml-2" role="status" aria-live="polite">
                        {customDateDisplay}
                    </span>
                )}
            </div>
            </div>
        </header>
    );
};

export default Header;